import { h, RefObject } from "preact";
import { useState, useEffect, useRef } from "preact/hooks";

import { Keyboard } from "../../../../foundation/enums";
import classname from "../../../../foundation/utils/classname";
import { cmdKey, ctrlOrWinKey } from "../../../../foundation/utils/keyboard";
import { highlightAndScrollToInput } from "../../../../foundation/utils/platform";
import IconSearch from "../../Icons/IconSearch";
import Input, { InputProps } from "..";

import styles from "./FilterInput.module.css";

type InheritedInputProps = Omit<InputProps, "icon" | "inputRef">;

interface Props extends InheritedInputProps {
    filterDelay?: number;
    shouldCaptureSearchShortcut?: boolean;
    shouldClearOnEscape?: boolean;
    hideIcon?: boolean;
    inputRef?: RefObject<HTMLInputElement>;
    onInput?: (ev: Event) => void;
    onFilter?: (value: string) => void;
    onKeyDown?: (ev: KeyboardEvent) => void;
}

const DEFAULT_FILTER_DELAY = 200; // ms

function FilterInput({
    class: cssClass,
    value: valueProp,
    filterDelay = DEFAULT_FILTER_DELAY,
    shouldCaptureSearchShortcut = true,
    shouldClearOnEscape = true,
    hideIcon = false,
    inputRef = useRef<HTMLInputElement>(null),
    onInput,
    onFilter,
    onKeyDown,
    ...inputProps
}: Props) {
    const [value, setValue] = useState(valueProp);

    const filterTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
    const lastFilterRef: RefObject<string> = useRef<string>(null);

    useEffect(() => {
        if (valueProp !== value) {
            setValue(valueProp);
        }
    }, [valueProp]);

    useEffect(() => {
        if (shouldCaptureSearchShortcut) {
            document.addEventListener("keydown", handleSearchShortcut);
        } else {
            document.removeEventListener("keydown", handleSearchShortcut);
        }

        return () => {
            document.removeEventListener("keydown", handleSearchShortcut);
        };
    }, [shouldCaptureSearchShortcut]);

    function handleFilter(filterValue: string) {
        if (onFilter && lastFilterRef.current !== filterValue) {
            lastFilterRef.current = filterValue;
            onFilter(filterValue);
        }
    }

    function handleInput(event: Event) {
        const inputValue = (event.target as HTMLInputElement).value;
        if (inputValue === value) {
            return;
        }

        setValue(inputValue);

        clearTimeout(filterTimeout.current);
        filterTimeout.current = setTimeout(() => {
            handleFilter(inputValue);
        }, filterDelay);

        onInput?.(event);
    }

    function handleSearchShortcut(ev: KeyboardEvent) {
        // Allow only Cmd/Ctrl + F
        if (cmdKey(ev) &&
            !(ctrlOrWinKey(ev) || ev.shiftKey || ev.altKey) &&
            ev.key === Keyboard.Key.f
        ) {
            ev.preventDefault();
            highlightAndScrollToInput(inputRef.current);
        }
    }

    function handleEscapeKey(ev: KeyboardEvent) {
        ev.preventDefault();
        ev.stopPropagation();

        clearTimeout(filterTimeout.current);

        if ((ev.target as HTMLInputElement).value) {
            setValue("");
        } else {
            (ev.target as HTMLInputElement).blur();
        }

        handleFilter("");
    }

    function handleKeyDown(ev: KeyboardEvent) {
        if (shouldClearOnEscape && ev.key === Keyboard.Key.Escape) {
            handleEscapeKey(ev);
        }

        onKeyDown?.(ev);
    }

    return (
        <Input
            {...inputProps}
            class={classname(styles.filterInput, cssClass, { [styles.disabled]: inputProps.disabled })}
            value={value}
            spellcheck={false}
            icon={!hideIcon ? IconSearch : undefined}
            inputRef={inputRef}
            onInput={handleInput}
            onKeyDown={handleKeyDown}/>
    );
}

export default FilterInput;
export type { Props as FilterInputProps };
